import { marked } from "marked";
import { isFollowableLink } from "./assistantLinks";

/* Stands in for an item's number until its list knows the position — `listitem` is called before
   `list` and is not told where it sits. */
const ITEM_MARK = "\u0000";

/**
 * Renders markdown to text instead of HTML: the marks go and the structure stays. Bold is its words,
 * a link is its words, a list keeps its numbers or dashes, a table is tab-separated so it pastes
 * into a spreadsheet as cells.
 *
 * The output is still entity-escaped the way marked escapes text for HTML, so it is decoded once at
 * the end — see answerToPlainText.
 */
const plainRenderer = {
    strong: (text) => text,
    em: (text) => text,
    del: (text) => text,
    codespan: (text) => text,
    /* Plain text has nowhere to hide an address, so a link keeps it beside its words — the profile
       link pasted into a message has to still go somewhere. Escaped like the rest of the text,
       which is decoded once at the end; a link that cannot be followed is its words only. */
    link: (href, title, text) =>
        isFollowableLink(href) && href.trim() !== text
            ? `${text} (${href
                  .trim()
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")})`
            : text,
    image: (href, title, text) => text,
    br: () => "\n",
    html: (html) => html,
    text: (text) => text,
    paragraph: (text) => `${text}\n\n`,
    heading: (text) => `${text}\n\n`,
    hr: () => "\n",
    blockquote: (quote) => quote,
    code: (code) => `${code}\n\n`,
    /* A nested list is rendered, numbers and all, before the item holding it, so its lines only
       need indenting under that item. A list opens on a new line because a tight item's text runs
       straight into the list nested in it. */
    listitem: (text) => `${ITEM_MARK}${text.trim().replace(/\n+/g, "\n   ")}\n`,
    list: (body, ordered, start) => {
        let n = typeof start === "number" ? start : 1;
        return `\n${body.replace(new RegExp(ITEM_MARK, "g"), () => (ordered ? `${n++}. ` : "- "))}\n`;
    },
    tablecell: (content) => `${content}\t`,
    tablerow: (content) => `${content.replace(/\t$/, "")}\n`,
    table: (header, body) => `${header}${body}\n`,
};

/**
 * The answer as someone would type it: what a plain-text field — a WhatsApp message, a CRM note, a
 * search box — receives when the answer is pasted there, rather than `**Imperum**` with its stars.
 *
 * The rendered text is decoded with DOMParser, which reads the entities back to characters and drops
 * any raw tag the model wrote, and runs nothing: a parsed document has no browsing context.
 */
export const answerToPlainText = (markdown) => {
    if (typeof markdown !== "string" || !markdown) return "";
    const rendered = marked.parse(markdown, {
        breaks: true,
        renderer: Object.assign(new marked.Renderer(), plainRenderer),
    });
    const text = new DOMParser().parseFromString(rendered, "text/html").documentElement.textContent || "";
    return text.replace(/\n{3,}/g, "\n\n").trim();
};

/**
 * Puts both readings of an answer on the clipboard, the way ChatGPT's copy does: the HTML for an
 * email or a document, which keeps the bold and the list, and the plain text for anywhere else.
 *
 * The Clipboard API first, then the old selection copy. The API needs the `clipboard-write`
 * permission, which a frame can be denied — the desktop app's own browser pane denies it outright —
 * while `execCommand("copy")` only needs the click it runs inside. The fallback sets both formats
 * from the copy event it fires, so it loses nothing.
 */
export const writeAnswerToClipboard = async ({ html, text }) => {
    try {
        if (typeof window.ClipboardItem === "function" && navigator.clipboard?.write) {
            await navigator.clipboard.write([
                new window.ClipboardItem({
                    "text/html": new Blob([html], { type: "text/html" }),
                    "text/plain": new Blob([text], { type: "text/plain" }),
                }),
            ]);
        } else {
            await navigator.clipboard.writeText(text);
        }
        return;
    } catch (e) {
        const fill = (event) => {
            event.clipboardData.setData("text/html", html);
            event.clipboardData.setData("text/plain", text);
            event.preventDefault();
        };
        const field = document.createElement("textarea");
        field.value = text;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        document.addEventListener("copy", fill);
        let copied = false;
        try {
            copied = document.execCommand("copy");
        } finally {
            document.removeEventListener("copy", fill);
            field.remove();
        }
        if (!copied) throw e;
    }
};
